import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Scale,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Filter,
  Clock,
  DollarSign,
  Percent,
  Users,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Eye
} from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface DriftAccount {
  accountId: number;
  accountName: string;
  accountNumber: string;
  householdName: string;
  modelName: string;
  modelId: number;
  currentValue: number;
  driftPercent: number;
  driftThreshold: number;
  driftAmount: number;
  daysOutOfSync: number;
  lastRebalanced: string;
  exceedsThreshold: boolean;
  complianceStatus: string;
  accountType: string;
  taxable: boolean;
}

interface DriftSummary {
  totalAccounts: number;
  exceedingThreshold: number;
  withinThreshold: number;
  totalAum: number;
}

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Container = styled.div`
  padding: 2rem;
  background: transparent;
  min-height: calc(100vh - 200px);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  color: #64748b;
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'warning' }>`
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          &:hover { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); }
        `;
      case 'warning':
        return `
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          &:hover { background: linear-gradient(135deg, #d97706 0%, #b45309 100%); }
        `;
      default:
        return `
          background: rgba(255, 255, 255, 0.05);
          color: #e2e8f0;
          border: 1px solid rgba(255, 255, 255, 0.1);
          &:hover { background: rgba(255, 255, 255, 0.1); }
        `;
    }
  }}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div<{ variant?: 'warning' | 'success' | 'info' }>`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'warning': return 'rgba(234, 179, 8, 0.3)';
      case 'success': return 'rgba(34, 197, 94, 0.3)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  border-radius: 12px;
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
`;

const StatIcon = styled.div<{ color: string }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.color || '#ffffff'};
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 0.625rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  option {
    background: #1e293b;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AccountsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const AccountRow = styled.div<{ exceeds?: boolean }>`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 100px;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  align-items: center;
  transition: background 0.15s;
  background: ${props => props.exceeds ? 'rgba(234, 179, 8, 0.05)' : 'transparent'};

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const AccountInfo = styled.div``;

const AccountName = styled.div`
  font-weight: 600;
  color: #ffffff;
  font-size: 0.9rem;
`;

const AccountMeta = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.25rem;
`;

const DriftCell = styled.div<{ exceeds?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const DriftValue = styled.span<{ exceeds?: boolean }>`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.exceeds ? '#eab308' : '#22c55e'};
`;

const DriftIndicator = styled.span<{ exceeds?: boolean }>`
  font-size: 0.7rem;
  color: ${props => props.exceeds ? '#eab308' : '#64748b'};
  margin-top: 0.125rem;
`;

const StatusBadge = styled.span<{ status: 'warning' | 'success' | 'neutral' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    switch (props.status) {
      case 'warning':
        return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
      case 'success':
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 100px;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatCurrency = (value: number) => {
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// ============================================================================
// COMPONENT
// ============================================================================

const DriftMonitorScreen: React.FC = () => {
  const [accounts, setAccounts] = useState<DriftAccount[]>([]);
  const [summary, setSummary] = useState<DriftSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'all' | 'exceeds' | 'within'>('all');
  const [filterModel, setFilterModel] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      const [accountsRes, summaryRes] = await Promise.all([
        axios.get('/api/advisor/rebalancing/account-drifts'),
        axios.get('/api/advisor/rebalancing/summary')
      ]);

      // Transform API data
      const transformedAccounts = accountsRes.data.map((d: any) => ({
        accountId: d.accountId,
        accountName: d.accountName,
        accountNumber: d.accountNumber || '',
        householdName: d.householdName || 'No Household',
        modelName: d.modelName,
        modelId: d.modelId,
        currentValue: parseFloat(d.currentValue) || 0,
        driftPercent: parseFloat(d.driftPercent) || 0,
        driftThreshold: parseFloat(d.driftThreshold) || 5,
        driftAmount: parseFloat(d.driftAmount) || 0,
        daysOutOfSync: d.daysOutOfSync || 0,
        lastRebalanced: d.lastRebalanced || '',
        exceedsThreshold: d.exceedsThreshold || false,
        complianceStatus: d.complianceStatus || 'UNKNOWN',
        accountType: d.accountType || '',
        taxable: d.taxable || false
      }));

      setAccounts(transformedAccounts);
      setSummary(summaryRes.data);

    } catch (error) {
      console.error('Error fetching drift data:', error);
      // Use mock data if API fails
      setAccounts([]);
      setSummary({
        totalAccounts: 0,
        exceedingThreshold: 0,
        withinThreshold: 0,
        totalAum: 0
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Get unique models for filter
  const models = useMemo(() => {
    const uniqueModels = new Map();
    accounts.forEach(a => {
      if (!uniqueModels.has(a.modelId)) {
        uniqueModels.set(a.modelId, { id: a.modelId, name: a.modelName });
      }
    });
    return Array.from(uniqueModels.values());
  }, [accounts]);

  // Filter accounts
  const filteredAccounts = useMemo(() => {
    return accounts.filter(account => {
      if (filterStatus === 'exceeds' && !account.exceedsThreshold) return false;
      if (filterStatus === 'within' && account.exceedsThreshold) return false;
      if (filterModel && account.modelId !== filterModel) return false;
      return true;
    });
  }, [accounts, filterStatus, filterModel]);

  // Sort by drift (highest first)
  const sortedAccounts = useMemo(() => {
    return [...filteredAccounts].sort((a, b) => b.driftPercent - a.driftPercent);
  }, [filteredAccounts]);

  if (loading) {
    return (
      <Container>
        <EmptyState>Loading drift monitor data...</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>
            <Scale className="w-6 h-6 text-amber-400" />
            Drift Monitor
          </Title>
          <Subtitle>
            Real-time monitoring of portfolio drift against model allocations
          </Subtitle>
        </div>
        <HeaderActions>
          <Button onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </HeaderActions>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatIcon color="#3b82f6">
            <Users className="w-5 h-5" />
          </StatIcon>
          <StatValue>{summary?.totalAccounts || 0}</StatValue>
          <StatLabel>Total Accounts</StatLabel>
        </StatCard>
        <StatCard variant={summary?.exceedingThreshold ? 'warning' : 'success'}>
          <StatIcon color="#eab308">
            <AlertTriangle className="w-5 h-5" />
          </StatIcon>
          <StatValue color={summary?.exceedingThreshold ? '#eab308' : '#22c55e'}>
            {summary?.exceedingThreshold || 0}
          </StatValue>
          <StatLabel>Exceeds Threshold</StatLabel>
        </StatCard>
        <StatCard variant="success">
          <StatIcon color="#22c55e">
            <CheckCircle className="w-5 h-5" />
          </StatIcon>
          <StatValue color="#22c55e">{summary?.withinThreshold || 0}</StatValue>
          <StatLabel>Within Tolerance</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#8b5cf6">
            <DollarSign className="w-5 h-5" />
          </StatIcon>
          <StatValue>{formatCurrency(summary?.totalAum || 0)}</StatValue>
          <StatLabel>Total AUM Monitored</StatLabel>
        </StatCard>
      </StatsGrid>

      <FiltersRow>
        <Filter className="w-4 h-4 text-slate-500" />
        <FilterSelect
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
        >
          <option value="all">All Accounts</option>
          <option value="exceeds">Exceeds Threshold</option>
          <option value="within">Within Tolerance</option>
        </FilterSelect>
        <FilterSelect
          value={filterModel || ''}
          onChange={(e) => setFilterModel(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">All Models</option>
          {models.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </FilterSelect>
        <span style={{ color: '#64748b', fontSize: '0.875rem', marginLeft: 'auto' }}>
          Showing {sortedAccounts.length} of {accounts.length} accounts
        </span>
      </FiltersRow>

      <Card>
        <CardHeader>
          <CardTitle>
            <Eye className="w-4 h-4 text-blue-400" />
            Account Drift Status
          </CardTitle>
        </CardHeader>
        <TableHeader>
          <div>Account</div>
          <div style={{ textAlign: 'right' }}>Value</div>
          <div style={{ textAlign: 'right' }}>Drift %</div>
          <div style={{ textAlign: 'right' }}>Days Out</div>
          <div style={{ textAlign: 'center' }}>Status</div>
          <div style={{ textAlign: 'center' }}>Model</div>
        </TableHeader>
        <AccountsList>
          {sortedAccounts.length === 0 ? (
            <EmptyState>
              {accounts.length === 0
                ? 'No accounts with model assignments found. Assign accounts to models to start monitoring drift.'
                : 'No accounts match the selected filters.'}
            </EmptyState>
          ) : (
            sortedAccounts.map(account => (
              <AccountRow key={account.accountId} exceeds={account.exceedsThreshold}>
                <AccountInfo>
                  <AccountName>{account.accountName}</AccountName>
                  <AccountMeta>
                    {account.accountNumber}
                    {account.householdName && ` • ${account.householdName}`}
                    {account.taxable && (
                      <span style={{ marginLeft: '0.5rem', padding: '0.125rem 0.375rem', background: 'rgba(234, 179, 8, 0.1)', borderRadius: '4px', color: '#eab308' }}>
                        Taxable
                      </span>
                    )}
                  </AccountMeta>
                </AccountInfo>
                <div style={{ textAlign: 'right', color: '#e2e8f0' }}>
                  {formatCurrency(account.currentValue)}
                </div>
                <DriftCell>
                  <DriftValue exceeds={account.exceedsThreshold}>
                    {account.driftPercent.toFixed(1)}%
                    {account.exceedsThreshold ? (
                      <ArrowUpRight className="w-4 h-4 inline ml-1" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4 inline ml-1" />
                    )}
                  </DriftValue>
                  <DriftIndicator exceeds={account.exceedsThreshold}>
                    Threshold: {account.driftThreshold}%
                  </DriftIndicator>
                </DriftCell>
                <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.375rem' }}>
                  <Clock className="w-3 h-3 text-slate-500" />
                  <span style={{ color: account.daysOutOfSync > 30 ? '#ef4444' : '#94a3b8' }}>
                    {account.daysOutOfSync}d
                  </span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <StatusBadge status={account.exceedsThreshold ? 'warning' : 'success'}>
                    {account.exceedsThreshold ? 'Needs Rebalance' : 'Compliant'}
                  </StatusBadge>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    color: '#3b82f6'
                  }}>
                    {account.modelName}
                  </span>
                </div>
              </AccountRow>
            ))
          )}
        </AccountsList>
      </Card>
    </Container>
  );
};

export default DriftMonitorScreen;
